import { FamilyMember } from "./familyTreeUtils";
import { getParents } from "./familyTreeMigrationUtils";

export interface GenerationalChain {
  id: string;
  name: string;
  description: string;
  members: FamilyMember[];
  startingMember: FamilyMember;
  generationCount: number;
  completeGenerationCount: number;
  lineType: "paternal" | "maternal";
}

export interface GenerationalArtData {
  selectedChain?: GenerationalChain;
  artTitle?: string;
  layoutType?: "horizontal" | "circular";
  numberOfGenerations?: number;
  submittedAt?: string;
}

/**
 * Build a generational chain following a specific family line
 */
export function buildGenerationalChain(
  familyData: FamilyMember[],
  startingMember: FamilyMember,
  lineType: "paternal" | "maternal",
  chainName: string
): GenerationalChain {
  const members: FamilyMember[] = [startingMember];
  // memberMap removed as it's no longer needed with getParents helper

  let currentMember = startingMember;
  let generation = 1;

  // Traverse up the family tree following the specified line
  let currentParents = getParents(currentMember);
  while (currentParents.length > 0) {
    let nextParent: FamilyMember | undefined;

    if (lineType === "paternal") {
      // Find father (assuming first parent is typically father)
      nextParent = currentParents[0];
    } else {
      // Find mother (assuming second parent is typically mother)
      nextParent = currentParents.length > 1 ? currentParents[1] : undefined;
    }

    if (!nextParent) {
      break;
    }

    members.push(nextParent);
    currentMember = nextParent;
    currentParents = getParents(currentMember);
    generation++;

    // Limit to reasonable number of generations
    if (members.length >= 7) {
      break;
    }
  }

  // Calculate how many generations are actually complete for generational art
  const completeMembers = members.filter((member) =>
    isMemberCompleteForGenerationalArt(member)
  );
  const completeGenerationCount = completeMembers.length;

  return {
    id: `${startingMember.id}-${lineType}`,
    name: chainName,
    description: `${
      lineType === "paternal" ? "Father's" : "Mother's"
    } line going back ${
      members.length
    } generations (${completeGenerationCount} complete)`,
    members,
    startingMember,
    generationCount: members.length,
    completeGenerationCount: completeGenerationCount,
    lineType,
  };
}

/**
 * Find all potential starting points for generational chains
 */
export function findPotentialGenerationalStarters(
  familyData: FamilyMember[],
  minGenerations: number = 3
): FamilyMember[] {
  return familyData.filter((member) => {
    const parents = getParents(member);

    // Check if this person has enough ancestors for both lines
    const paternalChain = buildGenerationalChain(
      familyData,
      member,
      "paternal",
      ""
    );

    const maternalChain = buildGenerationalChain(
      familyData,
      member,
      "maternal",
      ""
    );

    const qualifies =
      paternalChain.members.length >= minGenerations ||
      maternalChain.members.length >= minGenerations;

    return qualifies;
  });
}

/**
 * Generate all available generational chains from family tree data
 */
export function generateAvailableChains(
  familyData: FamilyMember[]
): GenerationalChain[] {
  if (!familyData.length) {
    return [];
  }

  const chains: GenerationalChain[] = [];
  const potentialStarters = findPotentialGenerationalStarters(familyData, 2);
  potentialStarters.forEach((starter, starterIndex) => {
    // Generate paternal line (following father's line)
    const paternalChain = buildGenerationalChain(
      familyData,
      starter,
      "paternal",
      `${starter.name}'s Paternal Line`
    );

    if (paternalChain.members.length >= 2) {
      chains.push(paternalChain);
    }

    // Generate maternal line (following mother's line)
    const maternalChain = buildGenerationalChain(
      familyData,
      starter,
      "maternal",
      `${starter.name}'s Maternal Line`
    );

    if (maternalChain.members.length >= 2) {
      chains.push(maternalChain);
    }
  });

  return chains;
}

/**
 * Check if a generational chain is ready for ordering
 */
export function isChainReadyForOrdering(chain: GenerationalChain): {
  ready: boolean;
  missingPhotos: number;
  missingNames: number;
  issues: string[];
} {
  const issues: string[] = [];
  let missingPhotos = 0;
  let missingNames = 0;

  chain.members.forEach((member, index) => {
    if (!member.name || member.name.trim() === "") {
      missingNames++;
      issues.push(`Generation ${index + 1}: Missing name`);
    }

    if (!member.imageMetadata) {
      missingPhotos++;
      issues.push(
        `Generation ${index + 1}: Missing photo for ${member.name || "Unknown"}`
      );
    }
  });

  const ready = missingPhotos === 0 && missingNames === 0;

  return {
    ready,
    missingPhotos,
    missingNames,
    issues,
  };
}

/**
 * Get a summary of a generational chain for display purposes
 */
export function getChainSummary(chain: GenerationalChain): {
  totalMembers: number;
  membersWithPhotos: number;
  membersWithNames: number;
  completionPercentage: number;
  generationSpan: string;
} {
  const totalMembers = chain.members.length;
  const membersWithPhotos = chain.members.filter((m) => m.imageMetadata).length;
  const membersWithNames = chain.members.filter(
    (m) => m.name && m.name.trim() !== ""
  ).length;

  const completionPercentage = Math.round(
    ((membersWithPhotos + membersWithNames) / (totalMembers * 2)) * 100
  );

  const generationSpan = `${chain.generationCount} generation${
    chain.generationCount > 1 ? "s" : ""
  }`;

  return {
    totalMembers,
    membersWithPhotos,
    membersWithNames,
    completionPercentage,
    generationSpan,
  };
}

/**
 * Convert generational art data to format suitable for order submission
 */
export function prepareGenerationalArtForOrder(
  generationalArtData: GenerationalArtData
): Record<string, any> | null {
  if (!generationalArtData.selectedChain) {
    return null;
  }

  const { selectedChain } = generationalArtData;

  return {
    chainId: selectedChain.id,
    chainName: selectedChain.name,
    lineType: selectedChain.lineType,
    numberOfGenerations: selectedChain.generationCount,
    startingMember: {
      id: selectedChain.startingMember.id,
      name: selectedChain.startingMember.name,
    },
    members: selectedChain.members.map((member, index) => ({
      id: member.id,
      name: member.name,
      generation: index + 1,
      birthdate: member.birthdate,
      deathdate: member.deathdate,
      hasPhoto: !!member.imageMetadata,
    })),
    artTitle: generationalArtData.artTitle,
    layoutType: generationalArtData.layoutType,
    submittedAt: generationalArtData.submittedAt,
  };
}

/**
 * Check if a family member is complete for generational art purposes
 */
export function isMemberCompleteForGenerationalArt(
  member: FamilyMember
): boolean {
  // Check if name is present and not a template placeholder
  const hasValidName =
    member.name &&
    member.name.trim() !== "" &&
    !member.name.includes("Name") && // Catches "Father's Name", "Mother's Name", etc.
    !member.name.includes("Placeholder");

  // Check if has a real photo (not placeholder)
  const hasValidPhoto =
    member.imageMetadata &&
    !member.imageMetadata.includes("Placeholder") &&
    member.imageMetadata.startsWith("https://"); // Real Firebase Storage URLs start with https://

  // For generational art, we need at minimum a name and photo
  return Boolean(hasValidName && hasValidPhoto);
}

// Utility functions for Generational Art functionality

// Interface for generational art order data
export interface GenerationalArtOrder {
  paypalOrderId: string;
  items: {
    productId: string;
    name: string;
    price: number;
    quantity: number;
    selectedOptions: Record<string, any>;
  }[];
  status: string;
  actionsRequired: string[];
  formData: Record<string, any>;
  createdAt: any;
  maxGenerations: number;
  isCompleted: boolean;
}

// Interface for order configuration state
export interface OrderConfiguration {
  orderId: string;
  selectedChainId: string;
  generationalArtData: GenerationalArtData;
  saving: boolean;
}

// Utility function to get generation count from order option
export const getGenerationCountFromOption = (generationOption: number): number => {
  return generationOption; // Direct integer value
};

// Helper function to format dates
export const formatOrderDate = (createdAt: any): string => {
  if (!createdAt) return "Unknown date";
  try {
    const date = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    return "Recently";
  }
};

// Helper function to get the correct chain description for the order
export const getChainDescriptionForOrder = (
  chain: any,
  maxGenerations: number
): string => {
  const effectiveGenerations = Math.min(
    chain.generationCount,
    maxGenerations
  );

  // Extract the base description and replace the generation count
  let description = chain.description || "";

  // Replace any generation count in the description with the effective count
  description = description.replace(
    /\d+ generations?/g,
    `${effectiveGenerations} generation${
      effectiveGenerations !== 1 ? "s" : ""
    }`
  );

  // If no generation count was found, add it
  if (!description.includes(`${effectiveGenerations} generation`)) {
    description = description.replace(
      /going back/,
      `going back ${effectiveGenerations} generation${
        effectiveGenerations !== 1 ? "s" : ""
      }`
    );
  }

  return description;
};

// Get filtered chains for a specific order
export const getFilteredChainsForOrder = (
  availableChains: GenerationalChain[],
  order: GenerationalArtOrder
): GenerationalChain[] => {
  // Instead of strict filtering, let's be more inclusive
  // Include all chains, but we'll indicate which ones have fewer generations than ordered
  const filtered = availableChains.filter(
    (chain) => chain.generationCount >= 2 // Only exclude chains with fewer than 2 generations
  );

  return filtered;
};

// Check if a chain is complete (has photos and metadata for all members)
export const checkChainCompleteness = (chain: any, maxGenerations: number) => {
  const membersToCheck = chain.members.slice(0, maxGenerations);
  const incompleteMembers = membersToCheck.filter((member: any) => {
    const hasPhoto =
      member.imageMetadata && !member.imageMetadata.includes("Placeholder");
    const hasName = member.name && member.name.trim() !== "";
    const hasBirthdate = member.birthdate && member.birthdate.trim() !== "";
    return !hasPhoto || !hasName || !hasBirthdate;
  });

  return {
    isComplete: incompleteMembers.length === 0,
    incompleteMembers,
    missingPhotos: incompleteMembers.filter(
      (m: any) => !m.imageMetadata || m.imageMetadata.includes("Placeholder")
    ),
    missingNames: incompleteMembers.filter(
      (m: any) => !m.name || m.name.trim() === ""
    ),
    missingBirthdates: incompleteMembers.filter(
      (m: any) => !m.birthdate || m.birthdate.trim() === ""
    ),
  };
};
